import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Icon from "@mui/material/Icon";
import MDBadge from "components/MDBadge";
import { Box, Stack, Typography, Tooltip, alpha } from "@mui/material";
import { useMaterialUIController } from "context";

function ComplexStatisticsCard({ color, title, icon, badge, menuDescription }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Use a static color map that works for both themes
  const colorMap = {
    primary: darkMode ? "#5c8db8" : "#1976d2",
    secondary: darkMode ? "#a07aab" : "#9c27b0",
    info: darkMode ? "#5a9bb5" : "#0288d1",
    success: darkMode ? "#5a9a5e" : "#2e7d32",
    warning: darkMode ? "#b8915a" : "#ed6c02",
    error: darkMode ? "#b56b6b" : "#d32f2f",
    light: darkMode ? "#9e9e9e" : "#757575",
    dark: darkMode ? "#b0b0b0" : "#424242",
  };

  const iconColor = colorMap[color] || colorMap.info;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        transition: "all 0.2s ease",
        backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
        borderColor: darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: iconColor,
          boxShadow: `0 4px 12px ${alpha(iconColor, 0.15)}`,
        },
      }}
    >
      <CardActionArea sx={{ height: "100%" }}>
        <CardContent>
          <Stack spacing={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: alpha(iconColor, darkMode ? 0.15 : 0.1),
                color: iconColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon fontSize="small">{icon}</Icon>
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ color: darkMode ? "#ffffff" : "rgba(0,0,0,0.87)" }}
              >
                {title}
                {badge && (
                  <Tooltip
                    title={
                      badge === "TEST"
                        ? "New updates are now available for testing"
                        : "Available in demo mode"
                    }
                    placement="top"
                  >
                    <MDBadge
                      badgeContent={badge}
                      color={badge === "TEST" ? "error" : "info"}
                      sx={{ ml: 1 }}
                    />
                  </Tooltip>
                )}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  minHeight: 40,
                  color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                }}
              >
                {menuDescription}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
};

export default ComplexStatisticsCard;
